import store from '@/store'
import panelManager from '@/core/helpers/panelManager'
import eventBus from '@/core/plugins/event-bus'
import i18n from '@/core/plugins/vue-i18n'
import { DOCK_ICONS } from '@/components/dock/dockIcons'

function navigateFileRelation(rel, router) {
    if (rel.type === 'MODULE_NOTE') {
        panelManager.openPanel('noteApp', { itemId: rel.id })
    } else if (rel.type === 'FILE_MANAGER_FOLDER') {
        panelManager.openPanel('fileManagerPanel')
    }
}

export default {
    platformType: 'company',

    getContextId() {
        return store.state.company?.authorized?.selectCompany || null
    },

    getContextParams() {
        const id = this.getContextId()
        return id != null ? { company_id: id } : {}
    },

    getMembers() {
        return store.state.company?.officer?.table?.data || []
    },

    async loadMembers() {
        await store.dispatch('company/officer/get')
    },

    mapMember(o) {
        return {
            id: o.user?.id || o.id,
            name: o.user?.full_name || o.full_name || ''
        }
    },

    getContextWatchPath() {
        return '$store.state.company.authorized.selectCompany'
    },

    getMembersWatchPath() {
        return '$store.state.company.officer.table.data'
    },

    getModuleCodes() {
        return store.getters['company/module/moduleCodes'] || []
    },

    async loadModules() {
        await store.dispatch('company/module/get')
    },

    getModulesData() {
        return store.state.company?.module?.table?.data || []
    },

    getModulesStorePath() {
        return (s) => s.company?.module?.table?.data
    },

    getNavigationMap() {
        return {
            CUSTOMERS:                  (item, router) => router.push('/customer/detail/' + item.id),
            VEHICLES:                   (item, router) => router.push('/customer/detail/' + item.meta?.company_customer_id),
            OFFICERS:                   () => null,
            STOCKS:                     (item, router) => router.push('/stock/detail/' + item.id),
            USERS:                      () => null,
            COMPANIES:                  () => null,
            FILES:                      (item, router) => {
                const relations = item.meta?.relations
                if (!relations || !relations.length) {
                    panelManager.openPanel('fileManagerPanel', { previewFileId: item.id })
                    return
                }
                if (relations.length === 1) {
                    navigateFileRelation(relations[0], router)
                } else {
                    const t = i18n.global.t
                    const truncate = (s, max) => s && s.length > max ? s.slice(0, max) + '…' : s
                    const buttons = [
                        ...relations.map(r => ({ label: truncate(r.label, 30), type: 'default', value: r })),
                        { label: t('common.cancel'), type: 'cancel' }
                    ]
                    eventBus.$emit('showConfirmDialog', {
                        title: item.title,
                        message: t('common.spotlight.fileMultipleRelations'),
                        buttons,
                        onConfirm: (rel) => navigateFileRelation(rel, router),
                    })
                }
            },
            FILE_FOLDERS:               (item) => panelManager.openPanel('fileManagerPanel', { folderId: item.id }),
            MODULE_VEHICLE_WORK_ORDER:  (item, router) => router.push('/module/vehicle-work-order/save/' + item.id),
            MODULE_NOTE:                (item) => panelManager.openPanel('noteApp', { itemId: item.id }),
            MODULE_AGENDA:              (item) => panelManager.openPanel('agendaPanel', { itemId: item.id, startDate: item.subtitle }),
            MODULE_WEATHER:             () => null,
            MODULE_MUSIC:               (item) => panelManager.openPanel('musicPanel', { trackId: item.id }),
            MODULE_MUSIC_PLAYLIST:      (item) => panelManager.openPanel('musicPanel', { playlistId: item.id }),
            MODULE_MUSIC_TRACKS:        (item) => panelManager.openPanel('musicPanel', { trackId: item.id }),
            MODULE_MUSIC_PLAYLISTS:     (item) => panelManager.openPanel('musicPanel', { playlistId: item.id }),
        }
    },

    getQuickActions() {
        return [
            { action: 'openNote', moduleCode: 'NOTE', icon: 'note', keywordsKey: 'keywordsNote' },
            { action: 'openAgenda', moduleCode: 'AGENDA', icon: 'agenda', keywordsKey: 'keywordsAgenda' },
            { action: 'newVWO', moduleCode: 'VEHICLE_WORK_ORDER', icon: 'workorder', keywordsKey: 'keywordsVWO' },
            { action: 'newSWO', moduleCode: 'SERVICE_WORK_ORDER', icon: 'workorder', keywordsKey: 'keywordsSWO' },
            { action: 'newCustomer', moduleCode: null, icon: 'customer', keywordsKey: 'keywordsCustomer' },
            { action: 'newOfficer', moduleCode: null, icon: 'officer', keywordsKey: 'keywordsOfficer' },
        ]
    },

    getModuleDockMap() {
        return {
            NOTE: { type: 'panel', id: 'noteApp', panelId: 'noteApp', labelKey: 'menu.NOTE', color: '#F59E0B', icon: DOCK_ICONS.noteApp },
            AGENDA: { type: 'panel', id: 'agendaPanel', panelId: 'agendaPanel', labelKey: 'menu.AGENDA', color: '#EF4444', icon: DOCK_ICONS.agendaPanel },
            VEHICLE_WORK_ORDER: { type: 'page', id: 'vehicleWorkOrder', route: '/module/vehicle-work-order', labelKey: 'menu.VEHICLE_WORK_ORDER', color: '#0EA5E9', icon: DOCK_ICONS.vehicleWorkOrder },
            MUSIC: { type: 'panel', id: 'musicPanel', panelId: 'musicPanel', labelKey: 'menu.MUSIC', color: '#8B5CF6', icon: DOCK_ICONS.musicPanel },
            WEATHER: { type: 'panel', id: 'weatherPanel', panelId: 'weatherPanel', labelKey: 'menu.WEATHER', color: '#38BDF8', icon: DOCK_ICONS.weatherPanel },
        }
    },

    getFileManagerConfig() {
        return {
            entities: true,
            storageQuota: true,
            entityModuleMap: {
                MODULE_NOTE: 'NOTE',
                MODULE_VEHICLE_WORK_ORDER: 'VEHICLE_WORK_ORDER',
                MODULE_MUSIC: 'MUSIC',
                MODULE_MUSIC_PLAYLIST: 'MUSIC',
            },
        }
    },

    getDockFixedItems() {
        return {
            pages: [
                { id: 'dashboard', route: '/dashboard', labelKey: 'menu.dashboard', color: '#3B82F6', icon: DOCK_ICONS.dashboard },
                { id: 'customers', route: '/customer', labelKey: 'menu.customerList', color: '#10B981', icon: DOCK_ICONS.customers },
                { id: 'stock', route: '/stock', labelKey: 'menu.stockList', color: '#8B5CF6', icon: DOCK_ICONS.product },
                { id: 'cashRegister', route: '/cash-register', labelKey: 'menu.cashRegister', color: '#F97316', icon: DOCK_ICONS.cashRegister },
                { id: 'modules', route: '/module', labelKey: 'menu.modules', color: '#EC4899', icon: DOCK_ICONS.modules },
                //{ id: 'billingPanel', panelId: 'billingPanel', labelKey: 'menu.BILLING', color: '#10B981', icon: DOCK_ICONS.billingPanel },
            ],
            panels: [
                { id: 'spotlight', panelId: 'spotlight', labelKey: 'common.spotlight.title', color: '#6366F1', icon: DOCK_ICONS.spotlight },
                { id: 'fileManagerPanel', panelId: 'fileManagerPanel', labelKey: 'menu.FILE_MANAGER', color: '#3B82F6', icon: DOCK_ICONS.fileManagerPanel },
            ],
        }
    },
}
